package com.eladfinish.bible.contest.features.review

import com.eladfinish.bible.contest.analytics.AnalyticsConstants
import com.eladfinish.bible.contest.analytics.crashlytics.ExceptionReporter
import com.eladfinish.bible.contest.analytics.providers.AnalyticsProvider
import io.github.aakira.napier.Napier
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds

/**
 * The one way into the in-app review request. Screens report success moments here; whether
 * and when the store's sheet is requested is decided in one place.
 */
interface ReviewPrompter {

    /**
     * A success the user is looking at right now (a finished game). Counted, and after a short
     * pause the review is requested if the user qualifies. Call from the screen's own scope, so
     * that leaving the screen during the pause cancels the request; it is then picked up by
     * [onMainMenuShown]. Once the store's flow has started, leaving the screen no longer stops it.
     *
     * @param grade 0..100 for a graded game; a grade below the configured minimum is no success.
     */
    suspend fun onSuccessMoment(moment: ReviewMoment, grade: Int? = null)

    /**
     * A success in the middle of a task (a search that found results). Counted now, but the
     * request waits for [onMainMenuShown], so it never covers what the user is reading.
     */
    suspend fun onDeferredSuccessMoment(moment: ReviewMoment)

    /** The user is back on the main menu: a natural pause. */
    suspend fun onMainMenuShown()

    /** The user opened the store listing themselves; don't ask again soon after. */
    fun onManualRateClicked()
}

object NoOpReviewPrompter : ReviewPrompter {
    override suspend fun onSuccessMoment(moment: ReviewMoment, grade: Int?) = Unit
    override suspend fun onDeferredSuccessMoment(moment: ReviewMoment) = Unit
    override suspend fun onMainMenuShown() = Unit
    override fun onManualRateClicked() = Unit
}

class DefaultReviewPrompter(
    private val launcher: InAppReviewLauncher,
    private val progress: ReviewProgress,
    private val shouldRequestReview: ShouldRequestReviewUseCase,
    private val configProvider: () -> InAppReviewConfig,
    private val analyticsProvider: AnalyticsProvider,
    private val exceptionReporter: ExceptionReporter,
    /** Debug builds only: skip every condition, to see the sheet on demand. */
    private val isForced: () -> Boolean,
    private val nowMillis: () -> Long,
    /**
     * Lives as long as the app. The store's flow runs here, so that a screen closed while it is
     * still running (Play's request and sheet take seconds) neither cuts it short nor loses its
     * outcome event.
     */
    private val requestScope: CoroutineScope,
    private val delayAfterSuccess: Duration = 2.seconds,
    private val delayOnMainMenu: Duration = 1.seconds,
    /** The store's review request is about to go out: other messages of the app wait for another session. */
    private val onRequested: () -> Unit = {},
) : ReviewPrompter {

    private val mutex = Mutex()

    // Both live for the process only: a success of a previous launch must not make the sheet
    // show up at the next launch, which is the one moment both stores say not to ask in.
    private var pendingMoment: ReviewMoment? = null
    private var requestedThisSession = false

    override suspend fun onSuccessMoment(moment: ReviewMoment, grade: Int?) {
        if (!recordSuccess(moment = moment, grade = grade)) return
        delay(delayAfterSuccess)
        requestIfEligible()
    }

    override suspend fun onDeferredSuccessMoment(moment: ReviewMoment) {
        recordSuccess(moment = moment, grade = null)
    }

    override suspend fun onMainMenuShown() {
        if (!launcher.isSupported || mutex.withLock { pendingMoment } == null) return
        delay(delayOnMainMenu)
        requestIfEligible()
    }

    override fun onManualRateClicked() {
        if (!launcher.isSupported) return
        progress.markRequested(nowMillis = nowMillis())
    }

    private suspend fun recordSuccess(moment: ReviewMoment, grade: Int?): Boolean {
        if (!launcher.isSupported) return false
        val config = configProvider()
        if (!config.enabled) return false
        if (grade != null && grade < config.minGrade) return false

        val successCount = mutex.withLock {
            pendingMoment = moment
            progress.recordSuccess(nowMillis = nowMillis())
        }
        analyticsProvider.logEvent(
            event = AnalyticsConstants.InAppReview.EVENT_SUCCESS_MOMENT,
            params = buildMap {
                put(AnalyticsConstants.InAppReview.PARAM_MOMENT, moment.analyticsName)
                put(AnalyticsConstants.InAppReview.PARAM_SUCCESS_COUNT, successCount)
                if (grade != null) put(AnalyticsConstants.InAppReview.PARAM_GRADE, grade)
            },
        )
        return true
    }

    private suspend fun requestIfEligible() {
        val moment = mutex.withLock {
            val pending = pendingMoment ?: return
            if (!isForced()) {
                if (requestedThisSession) return
                val eligibility = shouldRequestReview(config = configProvider())
                if (eligibility != ReviewEligibility.ELIGIBLE) {
                    Napier.d(message = "No review request after ${pending.analyticsName}: $eligibility", tag = TAG)
                    return
                }
            }
            pendingMoment = null
            requestedThisSession = true
            onRequested()
            progress.markRequested(nowMillis = nowMillis())
            pending
        }

        Napier.d(message = "Requesting in-app review after ${moment.analyticsName}", tag = TAG)
        analyticsProvider.logEvent(
            event = AnalyticsConstants.InAppReview.EVENT_PROMPT_REQUESTED,
            params = buildMap {
                put(AnalyticsConstants.InAppReview.PARAM_MOMENT, moment.analyticsName)
                put(AnalyticsConstants.InAppReview.PARAM_SUCCESS_COUNT, progress.successCount)
                shouldRequestReview.daysSinceInstall()?.let {
                    put(AnalyticsConstants.InAppReview.PARAM_DAYS_SINCE_INSTALL, it)
                }
            },
        )

        requestScope.launch { requestReview(moment = moment) }.join()
    }

    private suspend fun requestReview(moment: ReviewMoment) {
        val startMillis = nowMillis()
        val result = try {
            launcher.requestReview()
        } catch (e: CancellationException) {
            analyticsProvider.logEvent(
                event = AnalyticsConstants.InAppReview.EVENT_PROMPT_CANCELLED,
                params = mapOf(
                    AnalyticsConstants.InAppReview.PARAM_MOMENT to moment.analyticsName,
                    AnalyticsConstants.InAppReview.PARAM_DURATION_MS to nowMillis() - startMillis,
                ),
            )
            throw e
        } catch (e: Exception) {
            exceptionReporter.report(e)
            ReviewRequestResult.Failed(reason = e.message ?: e.toString())
        }

        when (result) {
            ReviewRequestResult.Requested -> Unit

            ReviewRequestResult.Finished -> analyticsProvider.logEvent(
                event = AnalyticsConstants.InAppReview.EVENT_PROMPT_FINISHED,
                params = mapOf(
                    AnalyticsConstants.InAppReview.PARAM_MOMENT to moment.analyticsName,
                    AnalyticsConstants.InAppReview.PARAM_DURATION_MS to nowMillis() - startMillis,
                ),
            )

            is ReviewRequestResult.Failed -> {
                Napier.w(message = "In-app review request failed: ${result.reason}", tag = TAG)
                analyticsProvider.logEvent(
                    event = AnalyticsConstants.InAppReview.EVENT_PROMPT_FAILED,
                    params = mapOf(
                        AnalyticsConstants.InAppReview.PARAM_MOMENT to moment.analyticsName,
                        AnalyticsConstants.InAppReview.PARAM_ERROR to result.reason.take(MAX_ERROR_LENGTH),
                    ),
                )
            }
        }
    }

    private companion object {
        const val TAG = "ReviewPrompter"

        // Firebase Analytics cuts a string parameter at 100 characters.
        const val MAX_ERROR_LENGTH = 100
    }
}
